export const METRIC_KEYS = [
  'ball_speed', 'club_speed', 'smash_factor', 'launch_angle', 'launch_direction',
  'spin_rate', 'spin_axis', 'carry', 'total', 'offline', 'apex', 'descent_angle',
  'attack_angle', 'club_path', 'face_angle', 'face_to_path', 'dynamic_loft',
] as const
export type MetricKey = (typeof METRIC_KEYS)[number]

// Canonical units from the server: speed mph, distance yards, angle degrees, spin rpm.
// Lateral signs follow the monitor export; consult the session's lateral_sign_verified flag.

export type DeviceOption = { key: string; label: string; export_help: string }

export type LaunchSessionSummary = {
  id: number
  name: string
  source?: 'csv' | 'photo' | 'averages'
  device_label: string | null
  lateral_sign_verified: boolean
  recorded_on: string | null
  shots_count: number
  clubs: string[]
  status: 'imported' | 'needs_mapping' | 'needs_review'
  created_at: string
}

export type LaunchSessionDetail = LaunchSessionSummary & {
  device?: string | null
  summary_only?: boolean
  summary_metrics?: { club: string | null; shots_reported: number | null; metrics: { field: MetricKey; label: string; value: number; unit: string }[] } | null
  equipment_notes: string | null
  row_errors: { row: number; reason: string }[]
  parse_warnings: string[]
}

export type Stat = { mean: number | null; sd: number | null; n: number }
export type Spread = { sd: number | null; min: number | null; max: number | null }

export type ClubSummary = {
  club: string
  shot_count: number
  included_count: number
  spin_estimated_count: number
  metrics: Record<MetricKey, Stat>
  spin_loft: Stat
  lateral_dispersion: Spread
  carry_dispersion: Spread
}

export type ShotRow = {
  id: number
  position: number
  club: string
  included: boolean
  auto_excluded: boolean
  user_inclusion: boolean | null
  spin_estimated: boolean | null
} & Record<MetricKey, number | null>

export type InclusionChoice = 'true' | 'false' | 'auto'

export type MappingField = {
  key: string
  label: string
  dimension: 'text' | 'speed' | 'distance' | 'angle' | 'spin' | 'ratio'
  units: string[]
  default_unit: string | null
}

export type ColumnChoice = { column: string; unit?: string | null }
export type ColumnMapping = Record<string, ColumnChoice>

export type Verdict = 'clear_difference' | 'possible_difference' | 'within_normal_variation' | 'not_enough_shots' | 'not_measured' | 'screen_averages'

export type ComparisonRow = {
  metric: MetricKey
  baseline_mean: number | null
  test_mean: number | null
  baseline_sd: number | null
  test_sd: number | null
  baseline_n: number | null
  test_n: number | null
  delta: number | null
  verdict: Verdict
  shots_needed: number
  missing_in: 'baseline' | 'test' | 'both' | null
  estimated_count: { baseline: number; test: number }
}
